/**
 * localStorage access that never throws.
 *
 * `window.localStorage` throws a SecurityError when the document has no access to it —
 * most notably a sandboxed iframe without `allow-same-origin` (e.g. an outside link
 * scanner or unfurler framing the app), and also private-mode / storage-disabled
 * browsers. These helpers degrade to null / no-ops so a storage touch on boot never
 * crashes a render or leaks an unhandled error.
 *
 * Note: this only guards our own access. Auth0's SDK (cacheLocation: 'localstorage')
 * reads localStorage internally, which we cannot wrap — blocking third-party framing at
 * the serving layer (X-Frame-Options / CSP frame-ancestors) is the definitive fix.
 */
export const safeLocalStorage = {
  getItem(key: string): string | null {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  },
  setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value)
    } catch {
      // storage unavailable (sandboxed iframe / private mode) — ignore
    }
  },
  removeItem(key: string): void {
    try {
      localStorage.removeItem(key)
    } catch {
      // storage unavailable — ignore
    }
  },
}
